// A small analytic renderer: sphere, metal stem, and rounded base. No model files
// or WebGL context are needed, and the same geometry works at export resolution.
const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const normalize = (v) => {
  const length = Math.hypot(...v);
  return v.map((value) => value / length);
};
const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];

const intersectEllipsoid = (origin, ray, center, radii) => {
  const p = origin.map((value, i) => (value - center[i]) / radii[i]);
  const d = ray.map((value, i) => value / radii[i]);
  const a = dot(d, d);
  const b = dot(p, d);
  const discriminant = b * b - a * (dot(p, p) - 1);
  if (discriminant < 0) return null;
  const t = (-b - Math.sqrt(discriminant)) / a;
  if (t <= 0) return null;
  const point = origin.map((value, i) => value + ray[i] * t);
  const normal = normalize(point.map((value, i) => (value - center[i]) / (radii[i] * radii[i])));
  return { t, point, normal };
};

const intersectStem = (origin, ray, top) => {
  const radius = 0.035;
  const bottom = 0.026;
  const a = ray[0] ** 2 + ray[1] ** 2;
  const b = origin[0] * ray[0] + origin[1] * ray[1];
  const discriminant = b * b - a * (origin[0] ** 2 + origin[1] ** 2 - radius ** 2);
  if (discriminant < 0 || a < 1e-9) return null;
  for (const t of [(-b - Math.sqrt(discriminant)) / a, (-b + Math.sqrt(discriminant)) / a]) {
    const point = origin.map((value, i) => value + ray[i] * t);
    if (t > 0 && point[2] >= bottom && point[2] <= top) return { t, point, normal: normalize([point[0], point[1], 0]) };
  }
  return null;
};

const MATERIALS = {
  "gold-pin": { base: [0.86, 0.6, 0.2], reflection: [1, 0.94, 0.75] },
  "silver-pin": { base: [0.49, 0.56, 0.65], reflection: [0.92, 0.97, 1] },
  "red-pin": { base: [0.65, 0.035, 0.028], reflection: [1, 0.88, 0.83] },
};
const cache = new Map();

export const getPinView = (element, scene) => {
  const horizontal = Math.max(-0.5, Math.min(0.5, element.x / scene.width - 0.5));
  const vertical = Math.max(-0.5, Math.min(0.5, element.y / scene.height - 0.5));
  // The camera looks toward the canvas center in both directions. Above and
  // below it, the raised head projects onto opposite sides of the pin's base.
  return normalize([-horizontal * 0.95, -vertical * 3.2, 1.15]);
};

export const renderPinMaterial = (element, scene, size) => {
  const horizontal = Math.max(-0.5, Math.min(0.5, element.x / scene.width - 0.5));
  const vertical = Math.max(-0.5, Math.min(0.5, element.y / scene.height - 0.5));
  const depth = element.depth ?? 1;
  const key = `${element.decoration}:${horizontal.toFixed(4)}:${vertical.toFixed(4)}:${depth.toFixed(3)}:${size}`;
  if (cache.has(key)) return cache.get(key);
  const view = getPinView(element, scene);
  const right = normalize([view[2], 0, -view[0]]);
  const down = cross(view, right);
  const ray = view.map((v) => -v);
  const light = normalize([-0.7, 0.2, 1.6]);
  const fillLight = normalize([0.85, 0.5, 0.65]);
  const half = normalize(light.map((v, i) => v + view[i]));
  const fillHalf = normalize(fillLight.map((v, i) => v + view[i]));
  const headCenter = [0, 0, 0.22 + 0.065 * depth];
  const headRadii = [0.15, 0.15, 0.15];
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext("2d");
  const image = context.createImageData(size, size);
  const material = MATERIALS[element.decoration] || MATERIALS["gold-pin"];
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const sx = ((x + 0.5) / size - 0.5) / 1.15;
      const sy = ((y + 0.5) / size - 0.5) / 1.15;
      const origin = view.map((value, i) => value * 2 + right[i] * sx + down[i] * sy);
      const head = intersectEllipsoid(origin, ray, headCenter, headRadii);
      const stem = intersectStem(origin, ray, headCenter[2]);
      const base = intersectEllipsoid(origin, ray, [0, 0, 0.03], [0.1, 0.1, 0.03]);
      let hit = head;
      let part = "head";
      if (stem && (!hit || stem.t < hit.t)) { hit = stem; part = "stem"; }
      if (base && (!hit || base.t < hit.t)) { hit = base; part = "base"; }
      if (!hit) continue;
      const {normal, point} = hit;
      const metal = part !== "head" && element.decoration === "red-pin" ? MATERIALS["silver-pin"] : material;
      let diffuse = Math.max(0, dot(normal, light));
      let specular = Math.pow(Math.max(0, dot(normal, half)), part === "head" ? 95 : 65);
      if (part !== "head") {
        const shadowOrigin = point.map((value, i) => value + normal[i] * 0.002);
        if (intersectEllipsoid(shadowOrigin, light, headCenter, headRadii)) { diffuse *= 0.24; specular *= 0.2; }
      }
      const broad = Math.pow(Math.max(0, dot(normal, half)), 8) * 0.3;
      const secondary = Math.pow(Math.max(0, dot(normal, fillHalf)), 16) * 0.12;
      const fresnel = Math.pow(1 - Math.max(0, dot(normal, view)), 4);
      const reflected = normal.map((value, i) => 2 * dot(normal, view) * value - view[i]);
      const horizon = Math.exp(-(((reflected[2] - 0.15) / 0.23) ** 2));
      const shade = 0.3 + diffuse * 0.65 + Math.max(0, normal[2]) * 0.1 + fresnel * 0.1;
      const index = (y * size + x) * 4;
      for (let channel = 0; channel < 3; channel += 1) {
        const value = metal.base[channel] * shade * (1 - horizon * 0.2)
          + metal.reflection[channel] * (specular * 1.1 + broad + secondary + fresnel * 0.09);
        image.data[index + channel] = Math.round(255 * Math.pow(Math.min(1, Math.max(0, value)), 0.8));
      }
      image.data[index + 3] = 255;
    }
  }
  context.putImageData(image, 0, 0);
  if (cache.size >= 24) cache.delete(cache.keys().next().value);
  cache.set(key, canvas);
  return canvas;
};
